import { Card, Button, Icon, Typography, Loader, LabelValue, Separator } from '@design-system/index';
import { ArchiveFilled, ArchiveLine, ModifierLine } from '@assets/images/icons';
import '../../DetailsPage.scss';
import { Grid } from '@design-system/components/Grid/Grid';
import { formatMoney } from '@utils/numbers';
import { useDetailsPreviewVM } from './useDetailsPreviewVM';
import { APARTMENT_TYPE } from '@utils/enumTranslation';
import { useNavigate } from 'react-router-dom';
import { ImageLazy } from '@components/ImageLazy/ImageLazy';

export default function DetailsPreview() {
    const { isLoading, isError, apartment } = useDetailsPreviewVM();

    const navigate = useNavigate();

    if (isLoading) {
        return <Loader className="full-container-loader" />;
    }

    if (isError) {
        return (
            <Typography intrinsic="p" color="secondary">
                Une erreur est survenue lors de la récupération des informations de la fiche logement
            </Typography>
        );
    }

    if (!apartment) {
        return <></>;
    }

    return (
        <div className="details-apartment-container">
            <div className="details-apartment-container-body">
                <Card
                    headerType="very-light"
                    headerTitle={apartment.fullname}
                    headerAction={
                         <Button variant="secondary" onClick={() => navigate(`/members-management/apartments/${apartment.id}/edit#main-section`)} icon={<Icon color="primary" icon={<ModifierLine />} size={16} />}>
                            Modifier
                        </Button>
                    }
                >
                    <Grid columns={3}>
                        <LabelValue label="Référence" value={apartment.reference} />
                        <LabelValue label="Intitulé du logement" value={apartment.label} />
                        <LabelValue label="Type de logement" value={APARTMENT_TYPE[apartment.type]} />
                    </Grid>
                </Card>

                <Card
                    headerType="very-light"
                    headerTitle="Informations du logement"
                    headerAction={
                        <Button variant="secondary" onClick={() => navigate(`/members-management/apartments/${apartment.id}/edit#informations-section`)} icon={<Icon color="primary" icon={<ModifierLine />} size={16} />}>
                           Modifier
                       </Button>
                   }
                >
                    <Grid columns={3}>
                        <LabelValue label="Adresse" value={apartment.address.address} />
                        <LabelValue label="Ville" value={apartment.address.city} />
                        <LabelValue label="Code postal" value={apartment.address.zipCode} />
                        <LabelValue label="Complément d'adresse" value={apartment.address.additionalAddress} />
                        <LabelValue label="Étage" value={apartment.floor} />
                    </Grid>
                    <Separator large />
                    <Grid columns={3}>
                        <LabelValue label="Superficie du logement" value={`${apartment.surface} m²`} />
                        <LabelValue label="Nb de pièces" value={apartment.numberOfRooms} />
                        <LabelValue label="Nb de couchages" value={apartment.numberOfBeds} />
                    </Grid>
                    {apartment.additionalDescription && <>
                        <Separator large />
                        <Grid columns={1}>
                            <LabelValue label="Description complémentaire" value={`${apartment.additionalDescription}`} />
                        </Grid>
                    </>}
                </Card>
               
                <Card
                    headerType="very-light"
                    headerTitle="Tarifs"
                    headerAction={
                        <Button variant="secondary" onClick={() => navigate(`/members-management/apartments/${apartment.id}/edit#tarifs-section`)} icon={<Icon color="primary" icon={<ModifierLine />} size={16} />}>
                           Modifier
                       </Button>
                   }
                >
                    <Grid columns={3}>
                        {apartment.apartmentPrices.map((apartmentPrice) => (
                            <LabelValue key={apartmentPrice.priceType.id} label={apartmentPrice.priceType.label} value={formatMoney(apartmentPrice.amount)} />
                        ))}
                        <LabelValue label="Caution dégâts" value={formatMoney(apartment.price.deposit)} />
                        <LabelValue label="Caution ménage" value={formatMoney(apartment.price.cleaningFee)} />
                        <LabelValue label="Caution annulation" value={formatMoney(apartment.price.cancellationFee)} />
                    </Grid>
                </Card>

                <Card
                    headerType="very-light"
                    headerTitle="Description"
                    headerAction={
                        <Button variant="secondary" onClick={() => navigate(`/members-management/apartments/${apartment.id}/edit#description-section`)} icon={<Icon color="primary" icon={<ModifierLine />} size={16} />}>
                           Modifier
                       </Button>
                   }
                >
                    <Typography intrinsic="p" variant="big" color="grey1" preLine dangerouslySetInnerHTML={{ __html: apartment.description || 'Aucune description' }} />
                </Card>

                <Card
                    headerType="very-light"
                    headerTitle="Photos"
                    headerAction={
                        <Button variant="secondary" onClick={() => navigate(`/members-management/apartments/${apartment.id}/edit#photos-section`)} icon={<Icon color="primary" icon={<ModifierLine />} size={16} />}>
                           Modifier
                       </Button>
                   }
                >
                    <div className="details-apartment-container-photos">
                        <div className="details-apartment-container-photos-grid">
                            {apartment.photos[0] && <ImageLazy radius={18} src={apartment.photos[0].viewLink} width={338} height={338} />}
                            {apartment.photos[1] && <ImageLazy radius={18} src={apartment.photos[1].viewLink} width={338} height={338} />}

                            {apartment.photos.length > 2 && <div className="details-apartment-container-photos-grid-items">
                                {apartment.photos.slice(2).map((photo) => (
                                    <ImageLazy key={photo.id} radius={18} src={photo.viewLink} width={161} height={161} />
                                ))}
                            </div>}
                        </div>
                    </div>
                </Card>
            </div>
            <div className="details-apartment-container-footer">
                <Button
                    variant="icon"
                    color="primary"
                    size="small"
                    hoverIcon={<Icon icon={<ArchiveFilled />} size={20} color="primary" />}
                    icon={<Icon icon={<ArchiveLine />} size={20} color="primary" />}
                >
                    <Typography intrinsic="p" variant="p2" color="primary">
                        Archiver
                    </Typography>
                </Button>
            </div>
        </div>
    );
}
